/**
 * Compliance > CoAs > Missing > detail. One expected purchase order that
 * should have a Certificate of Analysis but does not (the chase list).
 *
 * Shows the PO facts (received date, days overdue, vendor, item list), any
 * certificates already related to the PO, and the Waive / Un-waive action so
 * FSQA can clear a PO that is not expected to carry a certificate.
 */

import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, X } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { useSession } from '@/lib/useSession';
import { formatDate, formatDateTime } from '@/lib/dateUtils';
import { docTypeLabel, hasComplianceCoaWrite, statusLabel } from '@/lib/useCoas';
import {
  missingStatusLabel,
  useMissingCoaDetail,
  useWaiveExpectedPo,
  type MissingCoaDetail as MissingCoaDetailType,
} from '@/lib/useMissingCoas';

// Same tinted-pill family as the missing grid: missing=red (action needed),
// waived=slate (deliberately cleared), anything else=neutral gray.
const STATUS_TONES: Record<string, string> = {
  missing: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  not_missing: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300',
  waived: 'bg-slate-200 text-slate-700 dark:bg-slate-700/50 dark:text-slate-200',
};

function StatusPill({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300';
  return <Pill tone={tone}>{missingStatusLabel(status)}</Pill>;
}

export function MissingCoaDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const backSearch = searchParams.toString();
  const { user } = useSession();
  const canWrite = hasComplianceCoaWrite(user?.permissions, user?.superAdmin);

  const detailQ = useMissingCoaDetail(id);
  const waive = useWaiveExpectedPo();
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const backHref = {
    pathname: '/compliance/coas/missing',
    search: backSearch ? `?${backSearch}` : '',
  };

  const onConfirmWaive = (reason: string) => {
    if (!detailQ.data) return;
    setToast(null);
    waive.mutate(
      { id: detailQ.data.id, reason, waive: true },
      {
        onSuccess: () => {
          setToast({ kind: 'ok', text: 'PO waived.' });
          setWaiveOpen(false);
        },
        onError: (err) => setToast({ kind: 'err', text: `Waive failed: ${err.message}` }),
      },
    );
  };

  const onUnwaive = () => {
    if (!detailQ.data) return;
    setToast(null);
    waive.mutate(
      { id: detailQ.data.id, reason: '', waive: false },
      {
        onSuccess: () => setToast({ kind: 'ok', text: 'PO un-waived.' }),
        onError: (err) => setToast({ kind: 'err', text: `Un-waive failed: ${err.message}` }),
      },
    );
  };

  const detail = detailQ.data;

  return (
    <div className="pb-20">
      <Link
        to={backHref}
        className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
      >
        <ArrowLeft className="h-3 w-3" /> Back to Missing CoAs
      </Link>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Compliance &middot; Missing CoAs</p>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink font-mono break-all">
            {detail ? `PO ${detail.po_number}` : 'Expected PO'}
          </h1>
          {detail && <StatusPill status={detail.status} />}
        </div>
        {detail?.supplier_name && (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">{detail.supplier_name}</p>
        )}
      </div>

      {toast && (
        <div
          className={`mb-4 rounded-md border px-3 py-2 text-sm ${
            toast.kind === 'ok'
              ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/40 dark:text-green-200'
              : 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200'
          }`}
          role="status"
        >
          {toast.text}
        </div>
      )}

      {detailQ.isLoading ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
      ) : detailQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">Could not load: {detailQ.error.message}</p>
      ) : detail ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-5">
            <FieldsBlock detail={detail} />
            <ItemsBlock items={detail.item_codes_list} />
          </div>
          <div className="space-y-5">
            <RelatedCertsBlock detail={detail} />
            {canWrite && (
              <WaiveBlock
                detail={detail}
                saving={waive.isPending}
                onOpenWaive={() => setWaiveOpen(true)}
                onUnwaive={onUnwaive}
              />
            )}
          </div>
        </div>
      ) : null}

      {waiveOpen && detail && (
        <WaiveModal
          label={`PO ${detail.po_number}`}
          saving={waive.isPending}
          onConfirm={onConfirmWaive}
          onClose={() => setWaiveOpen(false)}
        />
      )}
    </div>
  );
}

function FieldsBlock({ detail }: { detail: MissingCoaDetailType }) {
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">Purchase order</h2>
      <dl className="space-y-2 text-sm">
        <ReadRow label="Received">{formatDate(detail.last_receipt_date)}</ReadRow>
        <ReadRow label="Days overdue">{detail.days_overdue ?? '--'}</ReadRow>
        <ReadRow label="PO date">{formatDate(detail.po_date)}</ReadRow>
        <ReadRow label="Vendor">
          {detail.supplier_name ?? '--'}
          {detail.vendor_no ? ` (${detail.vendor_no})` : ''}
        </ReadRow>
        <ReadRow label="Matched certs">{detail.matched_doc_count}</ReadRow>
      </dl>
    </div>
  );
}

function ItemsBlock({ items }: { items: string[] }) {
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">
        Items ({items.length})
      </h2>
      {items.length === 0 ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No item codes on this PO.</p>
      ) : (
        <ul className="text-sm text-amp-ink dark:text-amp-dark-ink space-y-1">
          {items.map((code) => (
            <li key={code} className="font-mono text-xs">{code}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RelatedCertsBlock({ detail }: { detail: MissingCoaDetailType }) {
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">
        Related certificates ({detail.related_certs.length})
      </h2>
      {detail.related_certs.length === 0 ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          No certificates are filed against this PO yet.
        </p>
      ) : (
        <ul className="divide-y divide-amp-line dark:divide-amp-dark-line">
          {detail.related_certs.map((cert) => (
            <li key={cert.document_id} className="py-2 first:pt-0 last:pb-0">
              <div className="flex items-center gap-2">
                <Link
                  to={`/compliance/coas/${cert.document_id}`}
                  className="text-sm text-amp-ink hover:underline dark:text-amp-dark-ink break-all"
                >
                  {cert.file_name}
                </Link>
                {cert.sharepoint_web_url && (
                  <a
                    href={cert.sharepoint_web_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in SharePoint"
                    className="text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
                  >
                    <ExternalLink className="h-3 w-3" aria-hidden />
                  </a>
                )}
              </div>
              <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {docTypeLabel(cert.doc_type)} &middot; {statusLabel(cert.status)} &middot; {formatDateTime(cert.received_at)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WaiveBlock({
  detail,
  saving,
  onOpenWaive,
  onUnwaive,
}: {
  detail: MissingCoaDetailType;
  saving: boolean;
  onOpenWaive: () => void;
  onUnwaive: () => void;
}) {
  const isMissing = detail.status === 'missing';
  const isWaived = detail.status === 'waived';
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">Waive</h2>
      {isWaived ? (
        <div className="space-y-3">
          <dl className="space-y-2 text-sm">
            {detail.waive_reason && <ReadRow label="Reason">{detail.waive_reason}</ReadRow>}
            {detail.waived_by && <ReadRow label="Waived by">{detail.waived_by}</ReadRow>}
            {detail.waived_at && <ReadRow label="Waived">{formatDateTime(detail.waived_at)}</ReadRow>}
          </dl>
          <button
            type="button"
            onClick={onUnwaive}
            disabled={saving}
            className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            {saving && <Loader2 className="h-3 w-3 animate-spin" />} Un-waive
          </button>
        </div>
      ) : isMissing ? (
        <div className="space-y-3">
          <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Waiving marks the PO as not applicable so it stops appearing on the missing list.
          </p>
          <button
            type="button"
            onClick={onOpenWaive}
            className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
          >
            Waive
          </button>
        </div>
      ) : (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No waive action available.</p>
      )}
    </div>
  );
}

function WaiveModal({
  label,
  saving,
  onConfirm,
  onClose,
}: {
  label: string;
  saving: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog">
      <div className="relative w-full max-w-md rounded-lg border border-amp-line bg-amp-white p-5 shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">Waive {label}</h2>
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-4">
          Waiving marks the PO as not applicable so it stops appearing on the missing list. Give a short reason for the record.
        </p>
        {/* Stays a plain box. Same field as the COA list: it goes into the XLSX
            export FSQA staff open in Excel, which cannot render HTML, and the
            column is only VARCHAR(500). */}
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Supplier exempt, certificate on file outside this system"
          className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={saving || !reason.trim()}
            className="text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface inline-flex items-center gap-1"
          >
            {saving && <Loader2 className="h-3 w-3 animate-spin" />}
            Waive
          </button>
        </div>
      </div>
    </div>
  );
}

function ReadRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-32 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</dt>
      <dd className="flex-1 text-amp-ink dark:text-amp-dark-ink break-words">{children}</dd>
    </div>
  );
}
